import { frontoffice } from 'core';
import { createChatService } from 'core/chatbot/services/chatService';
import { getAuthTokenFromStorage } from 'core/chatbot/services/serviceConfig';
import { useUser } from 'core/lite/hooks/useUser';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { getTenantChatConfig } from './tenantChatConfig';
import useIsAiEligible from './useIsAiEligible';

type CurrentEntity = { guid?: string; name?: string };

type AppStateish = {
  auth?: { user?: { guid?: string } };
};

function readMsalToken(): string | null {
  const msalStorage = localStorage.getItem('msal.token.keys');
  if (!msalStorage) return null;
  const keys = JSON.parse(msalStorage);
  if (!keys?.accessToken?.length) return null;
  const tokenData = localStorage.getItem(keys.accessToken[0]);
  if (!tokenData) return null;
  const token = JSON.parse(tokenData);
  return token?.secret ?? null;
}

export function useAIChat() {
  const config = getTenantChatConfig();
  const location = useLocation();

  const currentUser = useSelector((state: AppStateish) => state.auth?.user);
  const currentCourse = useSelector((state: unknown) => frontoffice.courses.selectors.getCurrentCourse(state)) as CurrentEntity | null;

  const { isStudent } = useUser();
  const isAiEnabled = useIsAiEligible();

  const getAuthToken = useCallback((): string | null => {
    try {
      if (config.shouldTryMsalToken) {
        const msalToken = readMsalToken();
        if (msalToken) return msalToken;
      }
      return getAuthTokenFromStorage();
    } catch (error) {
      console.error('Error getting auth token:', error);
      return null;
    }
  }, [currentUser?.guid, config.shouldTryMsalToken]);

  const getChatContextData = useCallback(() => {
    const context: Record<string, string | undefined> = {
      userGuid: currentUser?.guid,
      currentPage: globalThis.location.pathname,
      timestamp: new Date().toISOString(),
      ...config.contextExtras,
    };
    if (currentCourse?.guid) {
      context.courseGuid = currentCourse.guid;
      context.courseName = currentCourse.name;
    }
    return context;
  }, [currentUser, currentCourse, config.contextExtras]);

  const shouldRenderOnCurrentRoute = useMemo(() => {
    const currentPath = location.pathname;
    return !config.excludeRoutes.some((route) => currentPath === route || currentPath.startsWith(route));
  }, [location.pathname, config.excludeRoutes]);

  const shouldHideFloatingButton = useMemo(
    () => config.excludeRoutesWithFloatingButton.some((route) => route.test(location.pathname)),
    [location.pathname, config.excludeRoutesWithFloatingButton]
  );

  const chatService = useMemo(
    () =>
      createChatService({
        onError: (error: unknown) => {
          console.error('Chat Service Error:', error);
        },
        getAuthToken,
      }),
    [getAuthToken]
  );

  const initialChatbotState = useMemo(() => {
    const hasShellOverrides = Object.keys(config.shellOverrides).length > 0;
    return hasShellOverrides ? { shell: config.shellOverrides } : {};
    // Mode is NOT seeded: the composition root derives it from the URL (detectConversationMode),
    // which unlike the retired per-tenant detector also recognizes teacher-tools/revision routes.
  }, [config.shellOverrides]);

  const shouldHideChatbotUI = !isAiEnabled || shouldHideFloatingButton || !isStudent;

  return {
    chatService,
    className: config.className,
    getChatContextData,
    initialChatbotState,
    isAiEnabled,
    shouldHideChatbotUI,
    shouldRenderOnCurrentRoute,
  };
}
